import * as React from "react";
import { QueryClientProvider } from "@tanstack/react-query";
import {
  Navigate,
  NavLink,
  Outlet,
  RouterProvider,
  createBrowserRouter,
  useNavigate,
} from "react-router-dom";
import {
  AppShell,
  AuthProvider,
  LoginPage,
  Spinner,
  ToastProvider,
  Toaster,
  cn,
  queryClient,
  useAuth,
} from "@toss-ads/ui";
import { LeadsPage } from "./pages/LeadsPage";
import { StatsPage } from "./pages/StatsPage";
import {
  ImpersonateCallback,
  clearImpersonationActor,
  getImpersonationActor,
} from "./pages/ImpersonateCallback";

function LoginRoute() {
  const { user, isLoading } = useAuth();
  const navigate = useNavigate();

  if (isLoading) return <FullScreenSpinner />;
  if (user) return <Navigate to="/leads" replace />;

  return (
    <LoginPage
      appName="Skyventures Ads"
      onLoggedIn={() => navigate("/leads", { replace: true })}
    />
  );
}

function ProtectedRoute() {
  const { user, isLoading, logout } = useAuth();
  const actor = getImpersonationActor();

  if (isLoading) return <FullScreenSpinner />;
  if (!user) return <Navigate to="/login" replace />;

  return (
    <AppShell
      user={user}
      onLogout={() => void logout()}
      appName="Skyventures Ads"
      centerSlot={<ClientNav />}
    >
      {actor && (
        <div className="bg-warning text-ink px-4 py-2 text-[12px] flex items-center justify-between gap-3">
          <span>
            <strong>{actor}</strong> 으로서 <strong>{user.email}</strong> 시점을 보는 중입니다. 토큰 만료 시 자동 logout (최대 15분).
          </span>
          <button
            type="button"
            onClick={() => {
              clearImpersonationActor();
              void logout();
              window.close();
            }}
            className="underline hover:no-underline"
          >
            세션 종료
          </button>
        </div>
      )}
      <Outlet />
    </AppShell>
  );
}

function ClientNav() {
  const items = [
    { to: "/leads", label: "잠재고객" },
    { to: "/stats", label: "통계" },
    { to: "/absent", label: "부재" },
  ];
  return (
    <nav className="flex items-center gap-1">
      {items.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          className={({ isActive }) =>
            cn(
              "px-3 py-1.5 text-[13px] rounded-md transition-colors",
              isActive
                ? "bg-surface-muted text-ink font-medium"
                : "text-ink-muted hover:text-ink hover:bg-surface-muted",
            )
          }
        >
          {item.label}
        </NavLink>
      ))}
    </nav>
  );
}

function FullScreenSpinner() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-canvas text-ink-subtle">
      <Spinner size={24} />
    </div>
  );
}

const router = createBrowserRouter([
  { path: "/login", element: <LoginRoute /> },
  { path: "/impersonate", element: <ImpersonateCallback /> },
  {
    element: <ProtectedRoute />,
    children: [
      { index: true, element: <Navigate to="/leads" replace /> },
      { path: "leads", element: <LeadsPage /> },
      { path: "stats", element: <StatsPage /> },
      { path: "absent", element: <LeadsPage variant="absent" /> },
      { path: "*", element: <Navigate to="/leads" replace /> },
    ],
  },
]);

export function App(): React.ReactElement {
  return (
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <AuthProvider>
          <RouterProvider router={router} />
          <Toaster />
        </AuthProvider>
      </ToastProvider>
    </QueryClientProvider>
  );
}
